import React, { useState, useEffect } from 'react';
import { supabase, getUserCoins, useCoupon } from '../lib/supabase';
import CoinsManager from '../components/CoinsManager';
import { useNavigate, Link } from 'react-router-dom';
import { CheckCircle, AlertTriangle, X, RefreshCw, ArrowLeft, Sparkles, Gift } from 'lucide-react';
import UserProfileSection from '../components/UserProfileSection';
import { toast } from 'react-hot-toast';

// Función para formatear números con separador de miles
const formatNumber = (num: number): string => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const CoinsPage: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [userCoins, setUserCoins] = useState<number>(0);
  const navigate = useNavigate();
  // Estados para el cupón
  const [couponCode, setCouponCode] = useState('');
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [showCouponInput, setShowCouponInput] = useState(false);

  useEffect(() => {
    const getUser = async () => {
      const { data } = await supabase.auth.getUser();
      setUser(data.user);
      
      if (data.user) {
        await loadUserCoins(data.user.id);
      }
    };
    getUser();
  }, []);
  
  const loadUserCoins = async (userId: string) => {
    try {
      const userCoinsData = await getUserCoins(userId);
      
      if (userCoinsData) {
        setUserCoins(userCoinsData.balance || 0);
      }
    } catch (error) {
      console.error('Error cargando monedas:', error);
    }
  };

  const handleVerifyPayment = () => {
    navigate('/verify-payment');
  };

  // Verificar si hay una transacción pendiente en sessionStorage
  const hasPendingTransaction = () => {
    const pendingTransactionStr = sessionStorage.getItem('pendingTransaction');
    const paykuTransactionStr = sessionStorage.getItem('payku_transaction');
    return !!(pendingTransactionStr || paykuTransactionStr);
  };

  // Limpiar datos de transacción pendiente
  const clearPendingTransaction = () => {
    sessionStorage.removeItem('pendingTransaction');
    sessionStorage.removeItem('payku_transaction');
    // Forzar actualización del componente
    setUser({ ...user });
  };

  // Función para manejar la redención de cupones con protección contra múltiples clics
  const handleRedeemCoupon = async () => {
    if (!user) {
      toast.error('Debes iniciar sesión para usar cupones');
      return;
    }

    if (!couponCode.trim()) {
      toast.error('Por favor, ingresa un código de cupón válido');
      return;
    }

    // Verificar si hay otro proceso de canje en curso
    if (isValidatingCoupon) {
      toast.error('Procesamiento en curso, por favor espera un momento');
      return;
    }

    // Deshabilitar el botón inmediatamente
    setIsValidatingCoupon(true);
    
    // ID único para este toast para actualizarlo más tarde
    const toastId = toast.loading('Validando y procesando tu cupón...', {
      duration: 10000, // 10 segundos para dar tiempo a la operación
      position: 'top-center',
      style: {
        background: '#1e293b',
        color: '#e2e8f0',
        border: '1px solid #3b82f6',
        padding: '12px 16px',
      },
    });

    try {
      // Añadir pequeño retraso para evitar clics accidentales múltiples
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Actualizar mensaje de procesamiento
      toast.loading('Verificando validez del cupón...', { id: toastId });
      
      // Procesar cupón
      const result = await useCoupon(user.id, couponCode.trim());
      
      // Actualizar el toast según el resultado
      if (result.success) {
        // Mensaje de éxito con más detalles
        toast.success(result.message, {
          id: toastId,
          duration: 5000,
          icon: '🎉',
          style: {
            background: '#1e3a8a',
            color: '#ffffff',
            padding: '16px',
            fontWeight: 'bold',
          },
        });
        
        setCouponCode('');
        setShowCouponInput(false);
        
        // Retrasar la recarga del saldo para dar tiempo a que se complete la transacción
        await new Promise(resolve => setTimeout(resolve, 1000));
        await loadUserCoins(user.id);
      } else {
        // Mensaje de error más descriptivo
        toast.error(result.message, {
          id: toastId,
          duration: 7000,
          style: {
            background: '#7f1d1d',
            color: '#ffffff',
            padding: '16px',
          },
        });
      }
    } catch (error) {
      console.error('Error al canjear cupón:', error);
      toast.error('Error al procesar el cupón. Por favor, inténtalo de nuevo más tarde.', {
        id: toastId,
        duration: 5000,
      });
      
      // Asegurarse de cerrar cualquier toast de carga adicional
      toast.dismiss('coupon-loading');
    } finally {
      // Retrasar un poco la habilitación del botón para evitar clics accidentales
      setTimeout(() => {
        setIsValidatingCoupon(false);
        // Asegurarse de que todos los toasts de carga se cierren
        toast.dismiss('coupon-loading');
      }, 2000);
    }
  };

  // Función para renderizar la sección de cupones
  const renderCouponSection = () => {
    return (
      <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-700/30 rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <Gift className="h-5 w-5 text-purple-400 mr-2" />
            <h3 className="text-lg font-medium text-white">¿Tienes un Cupón?</h3>
          </div>
          <button 
            onClick={() => setShowCouponInput(!showCouponInput)}
            className="text-sm text-blue-400 hover:text-blue-300"
          >
            {showCouponInput ? 'Ocultar' : 'Canjear'}
          </button>
        </div>
        
        {showCouponInput && (
          <div className="mt-3">
            <div className="flex">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                placeholder="Ingresa tu código de cupón"
                className="flex-1 bg-gray-700 text-white border border-gray-600 rounded-l px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={isValidatingCoupon}
              />
              <button
                onClick={() => {
                  // Deshabilitar el botón inmediatamente mediante la actualización del estado
                  setIsValidatingCoupon(true);
                  // Mostrar un toast de carga que se cierra automáticamente cuando se procesa el cupón
                  const loadingToastId = toast.loading('Procesando cupón...', { id: 'coupon-loading' });
                  // Llamar a la función de manejo
                  handleRedeemCoupon().finally(() => {
                    // Asegurarse de cerrar el toast de carga inicial
                    toast.dismiss(loadingToastId);
                  });
                }}
                disabled={isValidatingCoupon || !couponCode.trim()}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-r flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isValidatingCoupon ? (
                  <span className="flex items-center">
                    <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                    Validando...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Sparkles className="h-4 w-4 mr-1" />
                    Canjear
                  </span>
                )}
              </button>
            </div>
            <p className="text-gray-400 text-xs mt-2">
              Ingresa un código de cupón válido para recibir monedas adicionales en tu cuenta.
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-8">
            <h1 className="text-2xl font-bold text-white mb-6 text-center">Comprar Monedas</h1>
            
            {/* Banner de verificación temporal (solo si hay transacción pendiente) */}
            {hasPendingTransaction() && (
              <div className="bg-blue-900/30 border border-blue-800 rounded-lg p-4 mb-8 relative">
                <button 
                  onClick={clearPendingTransaction}
                  className="absolute top-2 right-2 text-blue-400 hover:text-blue-300"
                  title="Cerrar"
                >
                  <X className="h-5 w-5" />
                </button>
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="h-6 w-6 text-blue-500" />
                  </div>
                  <div className="ml-3 flex-1 pr-6">
                    <h3 className="text-sm font-medium text-blue-300">¿Has realizado un pago recientemente?</h3>
                    <div className="mt-2 text-xs text-blue-200">
                      <p>
                        Si has completado un pago pero no has recibido tus monedas, puedes verificar el estado de tu pago.
                      </p>
                    </div>
                    <div className="mt-2">
                      <button
                        type="button"
                        onClick={handleVerifyPayment}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <CheckCircle className="mr-1 h-3 w-3" />
                        Verificar mi pago
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Contenido principal */}
            <div className="space-y-8">
              {/* Información de usuario con avatar */}
              <UserProfileSection user={user} coins={userCoins} />
              
              {/* Gestor de monedas con sección de cupones integrada */}
              <CoinsManager 
                user={user} 
                onCoinsUpdated={() => loadUserCoins(user?.id)} 
                renderCouponSection={renderCouponSection}
                formatNumber={formatNumber}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoinsPage; 